import { useEffect, useState } from 'react';
import { Box } from '@mantine/core';

import { useLazyLoadContest } from '~/domains/home/hooks/useLazyLoadContestSectionInfinite';

import { usePromotedMarkets } from '../../hooks/usePromotedMarkets';
import { useMarketLocationAllowed } from '../../hooks/useMarketLocationAllowed';
import { useMarketAccountSettings } from '../../hooks/useMarketAccountSettings';

import { CarouselSkeleton } from './CarouselSkeleton';
import { PromotedMarketsSection } from './PromotedMarketsSection';

const SETTLE_MS = 500;

export function HomePredictionMarkets({ order }: { order?: number }) {
  // PRD 4.1: an ineligible user sees no module at all. The account query decides that, and it
  // reports "allowed" before it resolves, so wait for it rather than render and then vanish.
  // Anonymous visitors never run it (`enabled: !!userId`), so they are never held up — and,
  // by the same token, can never be filtered out by state.
  const { isInitialLoading: isCheckingLocation } = useMarketAccountSettings();
  const { isAllowed: isLocationAllowed } = useMarketLocationAllowed();
  const isEligible = isLocationAllowed && !isCheckingLocation;

  // Below the fold the widget costs nothing: the request waits for the section to come near the
  // viewport, and the pusher subscriptions follow `isVisible` in and out.
  const { elementRef, isVisible } = useLazyLoadContest();
  const [hasBeenInView, setHasBeenInView] = useState(false);

  // The sections above render from async data, so at first paint the page is short and this one
  // briefly intersects even though it ends up below the fold — enough for the hook's own
  // `hasBeenVisible` to latch. Require the intersection to hold before fetching.
  useEffect(() => {
    if (hasBeenInView || !isVisible) return undefined;

    const timeout = setTimeout(() => setHasBeenInView(true), SETTLE_MS);

    return () => clearTimeout(timeout);
  }, [isVisible, hasBeenInView]);

  const { marketGroupIds, isLoading } = usePromotedMarkets({
    enabled: isEligible && hasBeenInView,
  });

  // PRD 4.1: once the check says no, the module is gone entirely.
  if (!isLocationAllowed) return null;

  // The lazy-load observer attaches on mount and never re-attaches, so the element it watches
  // has to exist from the very first render — including while the location check is in flight.
  if (isCheckingLocation || !hasBeenInView) {
    return (
      <Box component="section" style={{ order }} ref={elementRef}>
        <CarouselSkeleton />
      </Box>
    );
  }

  // A failed background refetch keeps the last good data, so hide on absence, not on error.
  if (!isLoading && !marketGroupIds.length) return null;

  // Rendered here rather than in a wrapper so a hidden widget leaves no empty flex item, which
  // the home container's row gap would otherwise show as a stray band.
  return (
    <Box component="section" style={{ order }} ref={elementRef}>
      <PromotedMarketsSection
        marketGroupIds={marketGroupIds}
        isLoading={isLoading}
        isVisible={isVisible}
      />
    </Box>
  );
}
